import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { PageHeader, Section, Field, Input, Textarea } from "@/components/Common";
import { Heart, ShieldCheck, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const TIERS = [
  { id: "supporter", amount: 25, label: "Supporter", desc: "Funds 1 student's program meal." },
  { id: "advocate", amount: 50, label: "Advocate", desc: "Sponsors a youth workshop seat." },
  { id: "patron", amount: 100, label: "Patron", desc: "Supports a community resource drive." },
  { id: "leader", amount: 250, label: "Leader", desc: "Backs a graduate scholarship in part." },
  { id: "founder", amount: 500, label: "Founder", desc: "Funds a full scholarship recognition." },
];

export function Donate() {
  const [selected, setSelected] = useState(50);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const amount = parseFloat(custom) > 0 ? parseFloat(custom) : selected;

  const submit = async (e) => {
    e.preventDefault();
    if (!amount || amount < 1) return toast.error("Please choose or enter a valid amount.");
    setLoading(true);
    try {
      const { data } = await api.post("/donate/checkout", {
        amount,
        donor_name: name || null,
        donor_email: email || null,
        message: message || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Donation failed to start.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="donate-page">
      <PageHeader eyebrow="Make a Gift" title="Your donation funds community, culture, and futures."
        subtitle="Every contribution is tax-deductible under our 501(c)(3) status. Donations process securely via Stripe." />

      <Section>
        <div className="grid lg:grid-cols-12 gap-12">
          <form onSubmit={submit} className="lg:col-span-7 space-y-7" data-testid="donate-form">
            <div>
              <div className="eyebrow mb-3">Choose an amount</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {TIERS.map((t) => (
                  <button type="button" key={t.id}
                    onClick={() => { setSelected(t.amount); setCustom(""); }}
                    data-testid={`donate-tier-${t.id}`}
                    className={`text-left p-5 rounded-2xl border transition ${
                      selected === t.amount && !custom
                        ? "border-[var(--bcol-navy)] bg-[var(--bcol-navy)] text-white"
                        : "border-[var(--bcol-border)] bg-white hover:border-[var(--bcol-navy)]"
                    }`}>
                    <div className="font-display text-2xl">${t.amount}</div>
                    <div className={`text-[11px] tracking-widest uppercase mt-1 ${selected === t.amount && !custom ? "text-[var(--bcol-gold)]" : "text-[var(--bcol-muted)]"}`}>{t.label}</div>
                    <div className={`text-xs mt-2 ${selected === t.amount && !custom ? "text-white/75" : "text-[var(--bcol-muted)]"}`}>{t.desc}</div>
                  </button>
                ))}
                <div className={`p-5 rounded-2xl border ${custom ? "border-[var(--bcol-navy)]" : "border-[var(--bcol-border)]"} bg-white`}>
                  <div className="text-[11px] tracking-widest uppercase text-[var(--bcol-muted)] mb-2">Custom (USD)</div>
                  <div className="flex items-center gap-1">
                    <span className="text-2xl font-display text-[var(--bcol-navy)]">$</span>
                    <input type="number" min="1" step="0.01" value={custom} onChange={(e) => setCustom(e.target.value)}
                      placeholder="0" className="w-full text-2xl font-display text-[var(--bcol-navy)] outline-none bg-transparent"
                      data-testid="donate-custom-amount" />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Your name (optional)"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" data-testid="donate-name" /></Field>
              <Field label="Email (for receipt)"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="donate-email" /></Field>
            </div>
            <Field label="Message (optional)">
              <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Dedicate this gift or share a note..." data-testid="donate-message" />
            </Field>

            <button disabled={loading} className="btn-gold w-full justify-center text-base" data-testid="donate-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />}
              {loading ? "Redirecting to secure checkout..." : `Donate $${amount?.toFixed ? amount.toFixed(2) : amount} now`}
            </button>
            <div className="flex items-center gap-2 text-xs text-[var(--bcol-muted)]"><ShieldCheck className="w-4 h-4 text-[var(--bcol-green)]" /> Payments processed securely by Stripe. We never store your card details.</div>
          </form>

          <aside className="lg:col-span-5">
            <div className="bg-[var(--bcol-navy)] text-white rounded-3xl p-8">
              <div className="eyebrow text-[var(--bcol-gold)] mb-3">Your impact</div>
              <h3 className="font-display text-3xl leading-tight mb-5">Every dollar reaches the community.</h3>
              <ul className="space-y-4 text-sm text-white/85">
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-[var(--bcol-gold)] shrink-0 mt-0.5" /> 100% of donations support BCOL programs — overhead is volunteer-funded.</li>
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-[var(--bcol-gold)] shrink-0 mt-0.5" /> Donations are tax-deductible.</li>
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-[var(--bcol-gold)] shrink-0 mt-0.5" /> Receipts emailed immediately after a successful donation.</li>
              </ul>
              <div className="mt-7 pt-7 border-t border-white/10">
                <div className="text-sm text-white/70 mb-2">Looking to give in another way?</div>
                <Link to="/sponsorship" className="text-[var(--bcol-gold)] hover:underline text-sm">→ Explore sponsorship opportunities</Link>
              </div>
            </div>
          </aside>
        </div>
      </Section>
    </div>
  );
}

export function DonateSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", data: null });

  useEffect(() => {
    if (!sessionId) { navigate("/donate"); return; }
    let attempts = 0;
    const max = 8;
    const poll = async () => {
      try {
        const { data } = await api.get(`/donate/status/${sessionId}`);
        if (data.payment_status === "paid") setState({ status: "paid", data });
        else if (data.status === "expired") setState({ status: "expired", data });
        else if (attempts >= max) setState({ status: "timeout", data });
        else { attempts++; setTimeout(poll, 2000); }
      } catch (e) {
        if (attempts >= max) setState({ status: "error", data: null });
        else { attempts++; setTimeout(poll, 2000); }
      }
    };
    poll();
  }, [sessionId, navigate]);

  return (
    <div data-testid="donate-success-page">
      <PageHeader eyebrow="Thank You" title="Your generosity changes lives." />
      <Section>
        <div className="max-w-2xl mx-auto card-soft p-10 text-center">
          {state.status === "checking" && (<>
            <Loader2 className="w-12 h-12 mx-auto text-[var(--bcol-navy)] animate-spin" />
            <p className="mt-5 text-[var(--bcol-muted)]">Confirming your donation...</p>
          </>)}
          {state.status === "paid" && (<>
            <div className="w-16 h-16 rounded-full bg-[var(--bcol-green)]/15 text-[var(--bcol-green)] flex items-center justify-center mx-auto"><CheckCircle2 className="w-9 h-9" /></div>
            <h3 className="font-display text-3xl text-[var(--bcol-navy)] mt-5">Donation Received</h3>
            <p className="text-[var(--bcol-muted)] mt-3">Thank you for supporting BCOL. A receipt is on its way to your inbox.</p>
            {state.data?.amount_total && <p className="mt-2 font-semibold text-[var(--bcol-navy)]">Amount: ${(state.data.amount_total / 100).toFixed(2)}</p>}
            <Link to="/" className="btn-primary mt-7" data-testid="donate-success-home">Back to home</Link>
          </>)}
          {(state.status === "expired" || state.status === "error") && (<>
            <h3 className="font-display text-2xl text-[var(--bcol-navy)]">Payment didn't complete</h3>
            <p className="text-[var(--bcol-muted)] mt-3">No charge was made. Please try again.</p>
            <Link to="/donate" className="btn-primary mt-6">Try again</Link>
          </>)}
          {state.status === "timeout" && (<>
            <h3 className="font-display text-2xl text-[var(--bcol-navy)]">Still processing</h3>
            <p className="text-[var(--bcol-muted)] mt-3">Your donation is being processed. Please check your email shortly.</p>
            <Link to="/" className="btn-primary mt-6">Back to home</Link>
          </>)}
        </div>
      </Section>
    </div>
  );
}
